import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Mail, Phone, MapPin, Plus, Download, Upload, ChevronLeft, ChevronRight, Trash2, X, CheckSquare, Square, User, Wrench, Building2 } from '../icons/wp-icons';
import { getAllKunden, createKunde, exportCsv, importCsv, getKundeById, bulkDeleteKunden } from '../api/kundenApi';
import Layout from '../components/Layout/Layout';
import SearchBar from '../components/Common/SearchBar';
import Modal from '../components/Common/Modal';
import KundenForm from '../components/Kunde/KundenForm';
import { useCustomerTransition } from '../context/CustomerTransitionContext';
import { useKundenRefresh } from '../hooks/useDataRefresh';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

function Kundenliste() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { startExpandTransition, transitionData, isAnimating, animationDirection } = useCustomerTransition();

  const [kunden, setKunden] = useState([]);
  const [filteredKunden, setFilteredKunden] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');

  // Bulk-Auswahl State
  const [selectedKunden, setSelectedKunden] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Animation State
  const [clickedCard, setClickedCard] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const cardRefs = useRef({});

  // Prefetch cache for smooth transitions
  const prefetchCache = useRef({});

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 21;

  // Neu laden wenn zur Seite navigiert wird (z.B. nach Kunde löschen)
  useEffect(() => {
    loadKunden();
  }, [location.key]);

  useEffect(() => {
    loadKunden();

    // Auto-Refresh wenn Tab/Fenster wieder fokussiert wird
    // Dadurch werden neue Kunden vom Email-Agent automatisch angezeigt
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadKunden();
      }
    };

    const handleFocus = () => {
      loadKunden();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    let filtered = kunden;

    if (statusFilter === 'hausverwaltung') {
      filtered = filtered.filter(kunde => kunde.kundenTyp === 'HAUSVERWALTUNG');
    } else if (statusFilter !== 'alle') {
      filtered = filtered.filter(kunde => kunde.status === statusFilter && kunde.kundenTyp !== 'HAUSVERWALTUNG');
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(kunde =>
        kunde.name.toLowerCase().includes(term) ||
        (kunde.email && kunde.email.toLowerCase().includes(term)) ||
        (kunde.telefon && kunde.telefon.includes(searchTerm)) ||
        (kunde.adresse && kunde.adresse.toLowerCase().includes(term)) ||
        (kunde.kundennummer && kunde.kundennummer.toLowerCase().includes(term))
      );
    }

    setFilteredKunden(filtered);
  }, [searchTerm, statusFilter, kunden]);

  // Reset auf Seite 1 wenn Suche oder Filter sich ändert
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Pagination Berechnung
  const totalPages = Math.ceil(filteredKunden.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedKunden = filteredKunden.slice(startIndex, endIndex);

  const loadKunden = async () => {
    try {
      const data = await getAllKunden();
      setKunden(data);
      setFilteredKunden(data);
    } catch (error) {
      console.error('Error loading kunden:', error);
    } finally {
      setLoading(false);
    }
  };

  // Echtzeit-Updates: Reagiere auf KI-Assistent Aktionen
  const handleKundenRefresh = useCallback(() => {
    loadKunden();
  }, []);

  useKundenRefresh(handleKundenRefresh);

  // URL-Parameter für "Neuer Kunde" Modal (von Keyboard Shortcut K)
  useEffect(() => {
    if (searchParams.get('neu') === 'true') {
      setIsModalOpen(true);
      // Parameter entfernen ohne Navigation
      searchParams.delete('neu');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Keyboard Shortcuts für Kundenliste
  const shortcuts = useMemo(() => ({
    'k': () => {
      if (!isModalOpen) {
        setIsModalOpen(true);
      }
    },
  }), [isModalOpen]);

  useKeyboardShortcuts(shortcuts, !isModalOpen);

  // Bulk-Auswahl Funktionen
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedKunden(new Set());
  };

  const toggleKundeSelection = (kundeId, event) => {
    event.stopPropagation();
    const newSelected = new Set(selectedKunden);
    if (newSelected.has(kundeId)) {
      newSelected.delete(kundeId);
    } else {
      newSelected.add(kundeId);
    }
    setSelectedKunden(newSelected);
  };

  const selectAllOnPage = () => {
    const newSelected = new Set(selectedKunden);
    paginatedKunden.forEach(kunde => newSelected.add(kunde.id));
    setSelectedKunden(newSelected);
  };

  const deselectAll = () => {
    setSelectedKunden(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedKunden.size === 0) return;

    const confirmMessage = selectedKunden.size === 1
      ? 'Möchten Sie diesen Kunden wirklich löschen? Alle zugehörigen Daten (Anfragen, Termine, Fotos, Dokumente) werden ebenfalls gelöscht.'
      : `Möchten Sie ${selectedKunden.size} Kunden wirklich löschen? Alle zugehörigen Daten (Anfragen, Termine, Fotos, Dokumente) werden ebenfalls gelöscht.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setIsDeleting(true);
      const result = await bulkDeleteKunden(Array.from(selectedKunden));
      toast.success(`${result.deleted} Kunden erfolgreich gelöscht.`);
      setSelectedKunden(new Set());
      setIsSelectionMode(false);
      await loadKunden();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Löschen der Kunden');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateKunde = async (kundeData) => {
    setIsSaving(true);
    setError('');
    setErrorCode('');
    try {
      await createKunde(kundeData);
      setIsModalOpen(false);
      await loadKunden();
    } catch (error) {
      console.error('Error creating kunde:', error);
      setError(error.response?.data?.error || 'Fehler beim Erstellen des Kunden');
      setErrorCode(error.response?.data?.code || '');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const blob = await exportCsv();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const datum = new Date().toISOString().split('T')[0];
      link.download = `Kunden_Export_${datum}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Fehler beim Exportieren der Kunden');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!window.confirm(`CSV-Datei "${file.name}" importieren?\n\nBestehende Kunden werden anhand der E-Mail-Adresse aktualisiert.`)) {
      e.target.value = '';
      return;
    }

    try {
      setIsImporting(true);
      const result = await importCsv(file);
      alert(`✅ Import erfolgreich!\n\nNeu erstellt: ${result.created}\nAktualisiert: ${result.updated}${result.errors > 0 ? `\nFehler: ${result.errors}` : ''}`);
      await loadKunden();
    } catch (error) {
      console.error('Error importing CSV:', error);
      alert(error.response?.data?.error || 'Fehler beim Importieren der CSV-Datei');
    } finally {
      setIsImporting(false);
      e.target.value = '';
    }
  };

  // Prefetch customer data on hover for smooth transition
  const handleCardHover = async (kundeId) => {
    if (!prefetchCache.current[kundeId]) {
      try {
        const fullData = await getKundeById(kundeId);
        prefetchCache.current[kundeId] = fullData;
      } catch (error) {
        // Silently fail - data will be loaded on navigation
      }
    }
  };

  // Handle card click with expand animation
  const handleCardClick = (kunde) => {
    const cardElement = cardRefs.current[kunde.id];
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();

      // Step 1: Card hebt sich an (visual feedback)
      setClickedCard(kunde.id);

      // Get prefetched data if available
      const prefetchedData = prefetchCache.current[kunde.id];

      // Step 2: Sofort Overlay zeigen und Animation starten
      setTimeout(() => {
        setShowOverlay(true);
        startExpandTransition(prefetchedData || kunde, rect);

        // Step 3: Schnell zur Detailseite navigieren
        setTimeout(() => {
          navigate(`/kunden/${kunde.id}`);
        }, 250);
      }, 50);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-8 flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold flex items-center gap-2">
              <User className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
              Kundenliste
            </h1>
            <p className="text-sm text-text-muted mt-1 sm:hidden">
              {filteredKunden.length} von {kunden.length} Kunden
            </p>
            {/* Mobile: Buttons unter Titel */}
            <div className="flex sm:hidden items-center gap-2 mt-3">
              <button
                onClick={toggleSelectionMode}
                className={`flex items-center gap-2 px-4 py-2 rounded-button font-medium transition ${
                  isSelectionMode
                    ? 'bg-primary text-white'
                    : 'bg-surface border border-border text-text'
                }`}
              >
                {isSelectionMode ? <X className="w-4 h-4" /> : <CheckSquare className="w-4 h-4" />}
                {isSelectionMode ? 'Abbrechen' : 'Auswählen'}
              </button>
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition"
              >
                <Plus className="w-5 h-5" />
                Neuer Kunde
              </button>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <div className="text-sm text-text-muted hidden sm:block">
              {filteredKunden.length} von {kunden.length} Kunden
            </div>

            {/* Auswahl-Modus Button */}
            <button
              onClick={toggleSelectionMode}
              className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-button font-medium transition ${
                isSelectionMode
                  ? 'bg-primary text-white'
                  : 'bg-surface border border-border text-text hover:bg-gray-50'
              }`}
            >
              {isSelectionMode ? <X className="w-4 h-4" /> : <CheckSquare className="w-4 h-4" />}
              {isSelectionMode ? 'Abbrechen' : 'Auswählen'}
            </button>

            {/* CSV Export Button - nur auf Desktop */}
            <button
              onClick={handleExport}
              disabled={isExporting || kunden.length === 0}
              className="hidden sm:flex items-center gap-2 bg-surface border border-border text-text px-4 py-2 rounded-button font-medium hover:bg-gray-50 transition disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {isExporting ? 'Exportiert...' : 'CSV Export'}
            </button>

            {/* CSV Import Button - nur auf Desktop */}
            <label className={`hidden sm:flex items-center gap-2 bg-surface border border-border text-text px-4 py-2 rounded-button font-medium hover:bg-gray-50 transition cursor-pointer ${isImporting ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <Download className="w-4 h-4" />
              {isImporting ? 'Importiert...' : 'CSV Import'}
              <input
                type="file"
                accept=".csv"
                onChange={handleImportFile}
                disabled={isImporting}
                className="hidden"
              />
            </label>

            {/* Desktop: Button rechts */}
            <button
              onClick={() => setIsModalOpen(true)}
              className="hidden sm:flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition"
            >
              <Plus className="w-5 h-5" />
              Neuer Kunde
            </button>
          </div>
        </div>

        {/* Search Bar + Filter */}
        <div className="mb-6 space-y-3">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Suche nach Name, E-Mail, Telefon, Adresse oder Kundennummer..."
          />
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'alle', label: 'Alle', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200', active: 'bg-gray-700 text-white', count: () => null },
              { value: 'neukunde', label: 'Neukunden', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100', active: 'bg-blue-600 text-white', count: () => kunden.filter(k => k.status === 'neukunde' && k.kundenTyp !== 'HAUSVERWALTUNG').length },
              { value: 'stammkunde', label: 'Stammkunden', color: 'bg-primary-light text-primary hover:bg-teal-200', active: 'bg-primary text-white', count: () => kunden.filter(k => k.status === 'stammkunde' && k.kundenTyp !== 'HAUSVERWALTUNG').length },
              { value: 'lieferant', label: 'Lieferanten', color: 'bg-amber-50 text-amber-700 hover:bg-amber-100', active: 'bg-amber-600 text-white', count: () => kunden.filter(k => k.status === 'lieferant').length },
              { value: 'hausverwaltung', label: 'Hausverwaltungen', color: 'bg-purple-50 text-purple-700 hover:bg-purple-100', active: 'bg-purple-600 text-white', count: () => kunden.filter(k => k.kundenTyp === 'HAUSVERWALTUNG').length },
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                  statusFilter === f.value ? f.active : f.color
                }`}
              >
                {f.label}
                {f.count() !== null && (
                  <span className="ml-1.5 opacity-70">
                    {f.count()}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Bulk-Aktionen-Leiste */}
        {isSelectionMode && (
          <div className="mb-6 bg-surface rounded-card shadow-card p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">
                {selectedKunden.size} von {filteredKunden.length} ausgewählt
              </span>
              <button
                onClick={selectAllOnPage}
                className="text-sm text-primary hover:underline"
              >
                Alle auf Seite auswählen
              </button>
              {selectedKunden.size > 0 && (
                <button
                  onClick={deselectAll}
                  className="text-sm text-text-muted hover:underline"
                >
                  Auswahl aufheben
                </button>
              )}
            </div>
            <button
              onClick={handleBulkDelete}
              disabled={selectedKunden.size === 0 || isDeleting}
              className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-button font-medium hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              {isDeleting ? 'Lösche...' : `${selectedKunden.size} Löschen`}
            </button>
          </div>
        )}

        {/* Kunden Grid */}
        {filteredKunden.length === 0 ? (
          <div className="bg-surface rounded-card shadow-card p-8 text-center">
            <p className="text-text-muted">
              {searchTerm ? 'Keine Kunden gefunden.' : 'Noch keine Kunden vorhanden.'}
            </p>
          </div>
        ) : (
          <>
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              animate={{
                opacity: showOverlay ? 0 : 1,
              }}
              transition={{ duration: 0.25 }}
            >
              {paginatedKunden.map((kunde) => (
                <motion.div
                  key={kunde.id}
                  ref={(el) => cardRefs.current[kunde.id] = el}
                  onClick={(e) => isSelectionMode ? toggleKundeSelection(kunde.id, e) : handleCardClick(kunde)}
                  onHoverStart={() => !isSelectionMode && handleCardHover(kunde.id)}
                  className={`bg-surface rounded-card shadow-card p-6 cursor-pointer relative ${
                    clickedCard === kunde.id ? 'invisible' : ''
                  } ${selectedKunden.has(kunde.id) ? 'ring-2 ring-primary' : ''}`}
                  whileHover={{
                    scale: isSelectionMode ? 1 : 1.02,
                    boxShadow: isSelectionMode ? undefined : '0 6px 20px rgba(0,0,0,0.1)',
                  }}
                  whileTap={{
                    scale: isSelectionMode ? 0.98 : 0.98,
                  }}
                  animate={{
                    scale: clickedCard === kunde.id ? 1.05 : 1,
                    boxShadow: clickedCard === kunde.id
                      ? '0 20px 60px rgba(0,0,0,0.2)'
                      : '0 4px 20px rgba(0,0,0,0.08)',
                  }}
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 25,
                  }}
                >
                  {/* Checkbox im Auswahl-Modus */}
                  {isSelectionMode && (
                    <div
                      className="absolute top-3 right-3 z-10"
                      onClick={(e) => toggleKundeSelection(kunde.id, e)}
                    >
                      {selectedKunden.has(kunde.id) ? (
                        <CheckSquare className="w-6 h-6 text-primary" />
                      ) : (
                        <Square className="w-6 h-6 text-gray-400 hover:text-primary" />
                      )}
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold">{kunde.name}</h3>
                    </div>
                    {!isSelectionMode && (
                      kunde.kundenTyp === 'HAUSVERWALTUNG' ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                          Hausverwaltung{kunde._count?.objekte > 0 ? ` (${kunde._count.objekte})` : ''}
                        </span>
                      ) : (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          kunde.status === 'stammkunde'
                            ? 'bg-primary-light text-primary'
                            : kunde.status === 'lieferant'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {kunde.status === 'stammkunde' ? 'Stammkunde' : kunde.status === 'lieferant' ? 'Lieferant' : 'Neukunde'}
                        </span>
                      )
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-text-muted">
                      <Mail className="w-4 h-4" />
                      {kunde.email && (kunde.email.endsWith('@temp.local') || kunde.email.endsWith('@import.local')) ? (
                        <span className="italic text-text-muted/70 truncate">Keine E-Mail hinterlegt</span>
                      ) : (
                        <span className="truncate">{kunde.email}</span>
                      )}
                    </div>

                    {kunde.telefon && (
                      <div className="flex items-center gap-2 text-sm text-text-muted">
                        <Phone className="w-4 h-4" />
                        <span>{kunde.telefon}</span>
                      </div>
                    )}

                    {kunde.adresse && (
                      <div className="flex items-center gap-2 text-sm text-text-muted">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate">{kunde.adresse}</span>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-4 text-xs text-text-muted">
                    <span>{kunde._count?.anfragen || 0} Anfragen</span>
                    <span>{kunde._count?.termine || 0} Termine</span>
                    <span>{kunde._count?.fotos || 0} Fotos</span>
                    <span>{kunde._count?.dokumente || 0} Dokumente</span>
                    {(kunde._count?.ansprechpartner || 0) > 0 && (
                      <span className="flex items-center gap-1 text-amber-600">
                        <Building2 className="w-3.5 h-3.5" /> {kunde._count.ansprechpartner} Ansprechpartner
                      </span>
                    )}
                    {(kunde._count?.anlagen || 0) > 0 && (
                      <span className="flex items-center gap-1">
                        <Wrench className="w-3 h-3" />
                        {kunde._count.anlagen} Anlagen
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 bg-surface rounded-card shadow-card px-4 sm:px-6 py-4">
                <div className="text-sm text-text-muted">
                  Zeige {startIndex + 1} - {Math.min(endIndex, filteredKunden.length)} von {filteredKunden.length} Kunden
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span className="hidden sm:inline">Zurück</span>
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        // Zeige erste, letzte, aktuelle und benachbarte Seiten
                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                      })
                      .map((page, idx, arr) => (
                        <span key={page} className="flex items-center">
                          {idx > 0 && arr[idx - 1] !== page - 1 && (
                            <span className="px-1 sm:px-2 text-text-muted">...</span>
                          )}
                          <button
                            onClick={() => setCurrentPage(page)}
                            className={`px-2 sm:px-3 py-2 text-sm rounded-button transition ${
                              currentPage === page
                                ? 'bg-primary text-white'
                                : 'hover:bg-gray-50 border border-border'
                            }`}
                          >
                            {page}
                          </button>
                        </span>
                      ))}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <span className="hidden sm:inline">Weiter</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Expand Animation Overlay */}
        <AnimatePresence>
          {showOverlay && transitionData && (
            <motion.div
              className="fixed inset-0 z-50 pointer-events-none"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {/* Background - matches the detail page background */}
              <motion.div
                className="absolute inset-0 bg-background"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15 }}
              />

              {/* Expanding Card */}
              <motion.div
                className="absolute bg-surface rounded-card shadow-2xl overflow-hidden"
                initial={{
                  top: transitionData.cardRect.top,
                  left: transitionData.cardRect.left,
                  width: transitionData.cardRect.width,
                  height: transitionData.cardRect.height,
                  borderRadius: 16,
                }}
                animate={{
                  top: 380,
                  left: '50%',
                  x: '-50%',
                  width: 'min(calc(100vw - 32px), 1200px)',
                  height: 'auto',
                  borderRadius: 16,
                }}
                transition={{
                  duration: 0.25,
                  ease: [0.32, 0.72, 0, 1],
                }}
              >
                <div className="p-4 sm:p-6" style={{ minHeight: '140px' }}>
                  {/* Header mit Name und Status */}
                  <div className="flex items-start justify-between mb-4">
                    <h1 className="text-xl sm:text-3xl font-serif font-bold">
                      {transitionData.kunde.name}
                    </h1>
                    {transitionData.kunde.kundenTyp === 'HAUSVERWALTUNG' ? (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-700">Hausverwaltung</span>
                    ) : (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        transitionData.kunde.status === 'stammkunde'
                          ? 'bg-primary-light text-primary'
                          : transitionData.kunde.status === 'lieferant'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {transitionData.kunde.status === 'stammkunde' ? 'Stammkunde' : transitionData.kunde.status === 'lieferant' ? 'Lieferant' : 'Neukunde'}
                      </span>
                    )}
                  </div>

                  {/* Kontaktdaten */}
                  <div className="space-y-2 text-text-muted">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {transitionData.kunde.email && (transitionData.kunde.email.endsWith('@temp.local') || transitionData.kunde.email.endsWith('@import.local')) ? (
                        <span className="italic text-text-muted/70">Keine E-Mail hinterlegt</span>
                      ) : (
                        <span>{transitionData.kunde.email}</span>
                      )}
                    </div>
                    {transitionData.kunde.telefon && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        <span>{transitionData.kunde.telefon}</span>
                      </div>
                    )}
                    {transitionData.kunde.adresse && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{transitionData.kunde.adresse}</span>
                      </div>
                    )}
                  </div>

                  {/* Notizen falls vorhanden */}
                  {transitionData.kunde.info && (
                    <motion.div
                      className="mt-4 pt-4 border-t border-border"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.2 }}
                    >
                      <h3 className="font-semibold mb-2">Notizen</h3>
                      <p className="text-text-muted whitespace-pre-wrap">{transitionData.kunde.info}</p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal for creating kunde */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setError('');
            setErrorCode('');
          }}
          title="Neuer Kunde"
        >
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              <p>{error}</p>
              {errorCode === 'KUNDEN_LIMIT_REACHED' && (
                <Link
                  to="/einstellungen/abonnement"
                  className="inline-block mt-2 text-primary hover:underline font-medium"
                >
                  Jetzt upgraden
                </Link>
              )}
            </div>
          )}
          <KundenForm
            onSubmit={handleCreateKunde}
            onCancel={() => {
              setIsModalOpen(false);
              setError('');
              setErrorCode('');
            }}
            isLoading={isSaving}
          />
        </Modal>
      </div>
    </Layout>
  );
}

export default Kundenliste;
